import React, { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { Box, Typography, Container, IconButton, Collapse, CircularProgress } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { styled } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import HeroSection from './components/HeroSection.tsx';
import ProblemSubmissionArea from './components/ProblemSubmissionArea.tsx';
import AdaptiveNavigation from './components/AdaptiveNavigation.tsx';
import FloatingNotifications from './components/FloatingNotifications.tsx';
import { getExecutivesBySection } from '../../data/executives.ts';
import { ExpandMore, ExpandLess, ArrowBack, Settings } from '@mui/icons-material';
import ExecutiveBoard from './components/ExecutiveBoard.tsx';
import { Executive } from '../../types/executive.ts';

// Lazy load components that aren't immediately visible
const SimilarProblems = lazy(() => import('./components/SimilarProblems.tsx'));
const ProblemSolvingToolbox = lazy(() => import('./components/ProblemSolvingToolbox.tsx'));

// Define user progress states
type UserProgressState = 'initial' | 'input' | 'submitted' | 'selected';
type SelectedSection = 'capital' | 'operations' | 'similar' | 'toolbox' | null;

// Styled components
const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  width: '100%',
  background: 'linear-gradient(135deg, rgba(138, 43, 226, 0.1), rgba(0, 204, 153, 0.8))',
  position: 'relative',
  overflow: 'hidden',
  padding: theme.spacing(4, 0),
}));

const BackgroundOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'radial-gradient(circle at center, rgba(138, 43, 226, 0.1), transparent 70%)',
  zIndex: 0,
}));

const ContentContainer = styled(Container)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,
  maxWidth: '1600px',
}));

// CSS Grid layout option
const GridLayout = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(12, 1fr)',
  gridGap: theme.spacing(3),
  alignItems: 'start',
}));

const LeftSidebarGrid = styled(Box)<{ isVisible: boolean }>(({ theme, isVisible }) => ({
  gridColumn: '1 / span 3',
  background: 'rgba(0, 51, 102, 0.3)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.3)',
  transition: 'all 0.5s ease',
  opacity: isVisible ? 1 : 0,
  transform: isVisible ? 'translateX(0)' : 'translateX(-20px)',
  pointerEvents: isVisible ? 'auto' : 'none',
  [theme.breakpoints.down('md')]: {
    gridColumn: '1 / span 12',
  },
}));

const MainContentGrid = styled(Box)<{ fullWidth: boolean }>(({ theme, fullWidth }) => ({
  gridColumn: fullWidth ? '1 / span 12' : '4 / span 6',
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('md')]: {
    gridColumn: '1 / span 12',
  },
}));

const RightSidebarGrid = styled(Box)<{ isVisible: boolean }>(({ theme, isVisible }) => ({
  gridColumn: '10 / span 3',
  background: 'rgba(0, 51, 102, 0.3)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  border: '1px solid rgba(0, 204, 153, 0.3)',
  transition: 'all 0.5s ease',
  opacity: isVisible ? 1 : 0,
  transform: isVisible ? 'translateX(0)' : 'translateX(20px)',
  pointerEvents: isVisible ? 'auto' : 'none',
  [theme.breakpoints.down('md')]: {
    gridColumn: '1 / span 12',
  },
}));

const BottomPanelGrid = styled(Box)(({ theme }) => ({
  gridColumn: '1 / span 12',
  background: 'rgba(26, 26, 46, 0.5)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  border: '1px solid rgba(255, 111, 0, 0.2)',
  transition: 'all 0.5s ease',
}));

const LeftSidebar = styled(Box)<{ isVisible: boolean }>(({ theme, isVisible }) => ({
  background: 'rgba(0, 51, 102, 0.3)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  height: '100%',
  border: '1px solid rgba(138, 43, 226, 0.3)',
  transition: 'all 0.5s ease',
  opacity: isVisible ? 1 : 0,
  transform: isVisible ? 'translateX(0)' : 'translateX(-20px)',
  pointerEvents: isVisible ? 'auto' : 'none',
}));

const RightSidebar = styled(Box)<{ isVisible: boolean }>(({ theme, isVisible }) => ({
  background: 'rgba(0, 51, 102, 0.3)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  height: '100%',
  border: '1px solid rgba(0, 204, 153, 0.3)',
  transition: 'all 0.5s ease',
  opacity: isVisible ? 1 : 0,
  transform: isVisible ? 'translateX(0)' : 'translateX(20px)',
  pointerEvents: isVisible ? 'auto' : 'none',
}));

const MainContent = styled(Box)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.5)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  transition: 'all 0.5s ease',
}));

const ExpandedMainContent = styled(MainContent)(({ theme }) => ({
  position: 'relative',
  minHeight: '70vh',
  display: 'flex',
  flexDirection: 'column',
}));

const BottomPanel = styled(Box)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.5)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  border: '1px solid rgba(255, 111, 0, 0.2)',
  transition: 'all 0.5s ease',
}));

const SidebarTitle = styled(Typography)(({ theme }) => ({
  color: '#fff',
  marginBottom: theme.spacing(2),
  fontWeight: 600,
  fontSize: '1.1rem',
}));

const ExpandButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(2),
  right: theme.spacing(2),
  backgroundColor: 'rgba(138, 43, 226, 0.2)',
  color: '#fff',
  '&:hover': {
    backgroundColor: 'rgba(138, 43, 226, 0.4)',
  },
  zIndex: 10,
}));

const BackButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  left: theme.spacing(2),
  backgroundColor: 'rgba(138, 43, 226, 0.2)',
  color: '#fff',
  '&:hover': {
    backgroundColor: 'rgba(138, 43, 226, 0.4)',
  },
  zIndex: 10,
}));

const SectionNavigation = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

const NavButton = styled(Box)<{ selected?: boolean }>(({ theme, selected }) => ({
  padding: theme.spacing(1.5, 3),
  borderRadius: theme.spacing(3),
  cursor: 'pointer',
  backgroundColor: selected ? 'rgba(138, 43, 226, 0.4)' : 'rgba(138, 43, 226, 0.1)',
  color: '#fff',
  fontWeight: selected ? 600 : 400,
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(138, 43, 226, 0.3)',
    transform: 'translateY(-2px)',
  },
}));

// Loading fallback component
const LoadingFallback = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(4),
  color: '#fff',
}));

// Screen reader only class
const ScreenReaderOnly = styled('span')({
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  borderWidth: 0,
});

// Helper function to convert status
const convertStatus = (status: string | undefined): 'available' | 'busy' | 'active' | 'idle' => {
  if (status === 'active') return 'available';
  if (status === 'busy' || status === 'idle' || status === 'available') {
    return status;
  }
  return 'available';
};

// Main component
const Home: React.FC = () => {
  // Refs for scrolling
  const problemSectionRef = useRef<HTMLDivElement>(null);
  const heroSectionRef = useRef<HTMLDivElement>(null);
  
  // State management
  const [notifications, setNotifications] = useState<Array<{ id: string; message: string; type: string }>>([]);
  const [problemStatement, setProblemStatement] = useState('');
  const [userProgress, setUserProgress] = useState<UserProgressState>('initial');
  const [selectedSection, setSelectedSection] = useState<SelectedSection>(null);
  const [isHeroExpanded, setIsHeroExpanded] = useState(false);
  const [showSidebars, setShowSidebars] = useState(true);
  
  // Get executives by section
  // Using the Executive type to ensure proper data structure:
  // Executive {
  //   id: string;
  //   name: string;
  //   title: string;
  //   avatarType?: string;
  //   section?: string;
  //   specialty?: string;
  //   status?: 'available' | 'busy' | 'active' | 'idle';
  //   rating?: number;
  //   completedTasks?: number;
  //   bio?: string;
  //   skills?: string[];
  //   role?: string;
  //   image?: string;
  // }
  const capitalExecutives = getExecutivesBySection('capital').map((exec, index) => ({
    ...exec,
    id: exec.id || `capital-exec-${index}`,
    title: exec.role || 'Capital Executive',
    specialty: exec.specialty || 'Strategic Leadership',
    status: convertStatus(exec.status),
    rating: exec.rating || 4.8,
    completedTasks: exec.completedTasks || 42
  })) as Executive[];

  const operationsExecutives = getExecutivesBySection('operations').map((exec, index) => ({
    ...exec,
    id: exec.id || `operations-exec-${index}`,
    title: exec.role || 'Operations Executive',
    specialty: exec.specialty || 'Technical Operations',
    status: convertStatus(exec.status),
    rating: exec.rating || 4.7,
    completedTasks: exec.completedTasks || 38
  })) as Executive[];
  
  // Add a notification
  const addNotification = (message: string, type: string = 'info') => {
    const id = Date.now().toString();
    setNotifications(prev => [...prev, { id, message, type }]);
    
    // Auto-remove notification after 5 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(notification => notification.id !== id));
    }, 5000);
  };
  
  // Handle problem submission
  const handleProblemSubmit = (problem: string) => {
    setProblemStatement(problem);
    setUserProgress('submitted');
    setShowSidebars(true);
    addNotification('Problem submitted successfully! Analyzing similar problems...', 'success');
    
    // Simulate AI analysis delay
    setTimeout(() => {
      addNotification('Found 3 similar problems and 5 potential solutions', 'info');
    }, 2000);
  };

  // Handle input method selected
  const handleInputMethodSelected = () => {
    if (userProgress === 'initial') {
      setUserProgress('input');
      setShowSidebars(false);
    }
  };

  // Handle section select
  const handleSectionSelect = (section: SelectedSection) => {
    setSelectedSection(section);
    setUserProgress('selected');
  };

  // Handle back button
  const handleBack = () => {
    if (userProgress === 'selected') {
      setUserProgress('submitted');
      setSelectedSection(null);
    } else if (userProgress === 'submitted') {
      setUserProgress('input');
      setShowSidebars(false);
    } else if (userProgress === 'input') {
      setUserProgress('initial');
      setShowSidebars(true);
      setProblemStatement('');
    }
  };

  // Smooth scroll to problem section
  const scrollToProblemSection = () => {
    if (problemSectionRef.current) {
      const yOffset = -80; // Adjust this value based on your header height
      const y = problemSectionRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
    }
  };

  // Toggle hero section expansion
  const toggleHeroExpansion = () => {
    setIsHeroExpanded(!isHeroExpanded);
  };

  // Effect to handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && (userProgress !== 'initial' || isHeroExpanded)) {
        if (isHeroExpanded) {
          setIsHeroExpanded(false);
        } else {
          handleBack();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [userProgress, isHeroExpanded]);

  // Toggle between Grid2 and CSS Grid
  const [useGridLayout, setUseGridLayout] = useState(true);

  return (
    <PageContainer>
      <BackgroundOverlay />
      
      <ContentContainer maxWidth={false}>
        {useGridLayout ? (
          <GridLayout>
            {/* Left Sidebar - Capital Focus */}
            {showSidebars && (
              <LeftSidebarGrid isVisible={showSidebars}>
                <SidebarTitle>Revenue Focused ExecOS</SidebarTitle>
                <Suspense fallback={
                  <LoadingFallback>
                    <CircularProgress size={30} color="secondary" />
                  </LoadingFallback>
                }>
                  <ExecutiveBoard 
                    section="capital"
                    executives={capitalExecutives}
                    onSelectExecutive={(executive) => {
                      addNotification(`Selected ${executive.name}, ${executive.title}`, 'info');
                    }}
                  />
                </Suspense>
              </LeftSidebarGrid>
            )}
            
            {/* Main Content Area */}
            <MainContentGrid fullWidth={!showSidebars}>
              <Box>
                {/* Hero Section */}
                <motion.div
                  layout
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                  ref={heroSectionRef}
                >
                  {isHeroExpanded ? (
                    <ExpandedMainContent>
                      {userProgress !== 'initial' && (
                        <BackButton onClick={handleBack} aria-label="Go back">
                          <ArrowBack />
                        </BackButton>
                      )}
                      <AnimatePresence mode="wait">
                        <motion.div
                          key={`hero-${userProgress}-${selectedSection}`}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
                        >
                          <HeroSection 
                            onGetStarted={scrollToProblemSection}
                            userProgress={userProgress}
                            selectedSection={selectedSection}
                            problemStatement={problemStatement}
                          />
                        </motion.div>
                      </AnimatePresence>
                      <ExpandButton onClick={toggleHeroExpansion} aria-label="Collapse hero section">
                        <ExpandLess />
                      </ExpandButton>
                    </ExpandedMainContent>
                  ) : (
                    <MainContent>
                      <motion.div 
                        initial={{ opacity: 0, y: -20 }} 
                        animate={{ opacity: 1, y: 0 }} 
                        transition={{ duration: 0.5 }}
                      >
                        <HeroSection 
                          onGetStarted={scrollToProblemSection}
                          userProgress={userProgress}
                          selectedSection={selectedSection}
                          problemStatement={problemStatement}
                        />
                        <ExpandButton onClick={toggleHeroExpansion} aria-label="Expand hero section">
                          <ExpandMore />
                        </ExpandButton>
                      </motion.div>
                    </MainContent>
                  )}
                </motion.div>
                
                {/* Section Navigation - Visible after submission */}
                {userProgress === 'submitted' && !isHeroExpanded && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <SectionNavigation role="navigation" aria-label="Problem analysis sections">
                      <NavButton 
                        selected={selectedSection === 'capital'}
                        onClick={() => handleSectionSelect('capital')}
                        role="button"
                        tabIndex={0}
                        aria-pressed={selectedSection === 'capital'}
                        onKeyDown={(e) => e.key === 'Enter' && handleSectionSelect('capital')}
                      >
                        Capital Focus
                      </NavButton>
                      <NavButton 
                        selected={selectedSection === 'operations'}
                        onClick={() => handleSectionSelect('operations')}
                        role="button"
                        tabIndex={0}
                        aria-pressed={selectedSection === 'operations'}
                        onKeyDown={(e) => e.key === 'Enter' && handleSectionSelect('operations')}
                      >
                        Operations Focus
                      </NavButton>
                      <NavButton 
                        selected={selectedSection === 'similar'}
                        onClick={() => handleSectionSelect('similar')}
                        role="button"
                        tabIndex={0}
                        aria-pressed={selectedSection === 'similar'}
                        onKeyDown={(e) => e.key === 'Enter' && handleSectionSelect('similar')}
                      >
                        Similar Problems
                      </NavButton>
                      <NavButton 
                        selected={selectedSection === 'toolbox'}
                        onClick={() => handleSectionSelect('toolbox')}
                        role="button"
                        tabIndex={0}
                        aria-pressed={selectedSection === 'toolbox'}
                        onKeyDown={(e) => e.key === 'Enter' && handleSectionSelect('toolbox')}
                      >
                        Problem Solving
                      </NavButton>
                    </SectionNavigation>
                  </motion.div>
                )}
                
                {/* Problem Submission Area */}
                <div ref={problemSectionRef} id="problem-submission" aria-labelledby="problem-submission-title">
                  <MainContent>
                    <motion.div 
                      initial={{ opacity: 0 }} 
                      animate={{ opacity: 1 }} 
                      transition={{ duration: 0.5, delay: 0.2 }}
                    >
                      <ScreenReaderOnly id="problem-submission-title">Problem Submission</ScreenReaderOnly>
                      <ProblemSubmissionArea 
                        onSubmit={handleProblemSubmit} 
                        onInputMethodSelected={handleInputMethodSelected}
                      />
                    </motion.div>
                  </MainContent>
                </div>
                
                {/* Similar Problems & Solutions - Conditionally rendered */}
                {userProgress === 'submitted' && (selectedSection === 'similar' || !selectedSection) && (
                  <MainContent>
                    <motion.div 
                      initial={{ opacity: 0 }} 
                      animate={{ opacity: 1 }} 
                      transition={{ duration: 0.5, delay: 0.4 }}
                    >
                      <Suspense fallback={
                        <LoadingFallback>
                          <CircularProgress size={30} color="secondary" />
                        </LoadingFallback>
                      }>
                        <SimilarProblems problemStatement={problemStatement} />
                      </Suspense>
                    </motion.div>
                  </MainContent>
                )}
                
                {/* Selected Section Content - Conditionally rendered */}
                {userProgress === 'selected' && selectedSection && (
                  <MainContent>
                    <motion.div 
                      initial={{ opacity: 0 }} 
                      animate={{ opacity: 1 }} 
                      transition={{ duration: 0.5 }}
                    >
                      {selectedSection === 'capital' && (
                        <Box>
                          <SidebarTitle>Capital Focus</SidebarTitle>
                          <Suspense fallback={
                            <LoadingFallback>
                              <CircularProgress size={30} color="secondary" />
                            </LoadingFallback>
                          }>
                            <ExecutiveBoard 
                              section="capital"
                              executives={capitalExecutives}
                              onSelectExecutive={(executive) => {
                                addNotification(`Selected ${executive.name}, ${executive.title}`, 'info');
                              }}
                            />
                          </Suspense>
                        </Box>
                      )}
                      {selectedSection === 'operations' && (
                        <Box>
                          <SidebarTitle>Operations Focused ExecOS</SidebarTitle>
                          <Suspense fallback={
                            <LoadingFallback>
                              <CircularProgress size={30} color="secondary" />
                            </LoadingFallback>
                          }>
                            <ExecutiveBoard 
                              section="operations"
                              executives={operationsExecutives}
                              onSelectExecutive={(executive) => {
                                addNotification(`Selected ${executive.name}, ${executive.title}`, 'info');
                              }}
                            />
                          </Suspense>
                        </Box>
                      )}
                      {selectedSection === 'similar' && (
                        <Suspense fallback={
                          <LoadingFallback>
                            <CircularProgress size={30} color="secondary" />
                          </LoadingFallback>
                        }>
                          <SimilarProblems problemStatement={problemStatement} />
                        </Suspense>
                      )}
                      {selectedSection === 'toolbox' && (
                        <Suspense fallback={
                          <LoadingFallback>
                            <CircularProgress size={30} color="secondary" />
                          </LoadingFallback>
                        }>
                          <ProblemSolvingToolbox problemStatement={problemStatement} />
                        </Suspense>
                      )}
                    </motion.div>
                  </MainContent>
                )}
              </Box>
            </MainContentGrid>
            
            {/* Right Sidebar - Operations Focus */}
            {showSidebars && (
              <RightSidebarGrid isVisible={showSidebars}>
                <SidebarTitle>Operations Focused ExecOS</SidebarTitle>
                <Suspense fallback={
                  <LoadingFallback>
                    <CircularProgress size={30} color="secondary" />
                  </LoadingFallback>
                }>
                  <ExecutiveBoard 
                    section="operations"
                    executives={operationsExecutives}
                    onSelectExecutive={(executive) => {
                      addNotification(`Selected ${executive.name}, ${executive.title}`, 'info');
                    }}
                  />
                </Suspense>
              </RightSidebarGrid>
            )}
            
            {/* Bottom Panel - Problem Solving Toolbox */}
            <BottomPanelGrid>
              <Collapse in={userProgress === 'submitted' || userProgress === 'selected'}>
                <BottomPanel>
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }} 
                    animate={{ opacity: 1, y: 0 }} 
                    transition={{ duration: 0.5, delay: 0.6 }}
                  >
                    <Suspense fallback={
                      <LoadingFallback>
                        <CircularProgress size={30} color="secondary" />
                      </LoadingFallback>
                    }>
                      <ProblemSolvingToolbox problemStatement={problemStatement} />
                    </Suspense>
                  </motion.div>
                </BottomPanel>
              </Collapse>
            </BottomPanelGrid>
          </GridLayout>
        ) : (
          <Grid container spacing={3} alignItems="flex-start">
            {/* Left Sidebar - Capital Focus */}
            {showSidebars && (
              <Grid xs={12} md={3}>
                <LeftSidebar isVisible={showSidebars}>
                  <SidebarTitle>Revenue Focused ExecOS</SidebarTitle>
                  <Suspense fallback={
                    <LoadingFallback>
                      <CircularProgress size={30} color="secondary" />
                    </LoadingFallback>
                  }>
                    <ExecutiveBoard 
                      section="capital"
                      executives={capitalExecutives}
                      onSelectExecutive={(executive) => {
                        addNotification(`Selected ${executive.name}, ${executive.title}`, 'info');
                      }}
                    />
                  </Suspense>
                </LeftSidebar>
              </Grid>
            )}
            
            {/* Main Content Area */}
            <Grid xs={12} md={showSidebars ? 6 : 12}>
              {/* Hero Section */}
              <motion.div
                layout
                transition={{ duration: 0.5, ease: "easeInOut" }}
                ref={heroSectionRef}
              >
                {isHeroExpanded ? (
                  <ExpandedMainContent>
                    {userProgress !== 'initial' && (
                      <BackButton onClick={handleBack} aria-label="Go back">
                        <ArrowBack />
                      </BackButton>
                    )}
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={`hero-${userProgress}-${selectedSection}`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
                      >
                        <HeroSection 
                          onGetStarted={scrollToProblemSection}
                          userProgress={userProgress}
                          selectedSection={selectedSection}
                          problemStatement={problemStatement}
                        />
                      </motion.div>
                    </AnimatePresence>
                    <ExpandButton onClick={toggleHeroExpansion} aria-label="Collapse hero section">
                      <ExpandLess />
                    </ExpandButton>
                  </ExpandedMainContent>
                ) : (
                  <MainContent>
                    <motion.div 
                      initial={{ opacity: 0, y: -20 }} 
                      animate={{ opacity: 1, y: 0 }} 
                      transition={{ duration: 0.5 }}
                    >
                      <HeroSection 
                        onGetStarted={scrollToProblemSection}
                        userProgress={userProgress}
                        selectedSection={selectedSection}
                        problemStatement={problemStatement}
                      />
                      <ExpandButton onClick={toggleHeroExpansion} aria-label="Expand hero section">
                        <ExpandMore />
                      </ExpandButton>
                    </motion.div>
                  </MainContent>
                )}
              </motion.div>
              
              {/* Section Navigation - Visible after submission */}
              {userProgress === 'submitted' && !isHeroExpanded && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <SectionNavigation role="navigation" aria-label="Problem analysis sections">
                    <NavButton 
                      selected={selectedSection === 'capital'}
                      onClick={() => handleSectionSelect('capital')}
                      role="button"
                      tabIndex={0}
                      aria-pressed={selectedSection === 'capital'}
                      onKeyDown={(e) => e.key === 'Enter' && handleSectionSelect('capital')}
                    >
                      Capital Focus
                    </NavButton>
                    <NavButton 
                      selected={selectedSection === 'operations'}
                      onClick={() => handleSectionSelect('operations')}
                      role="button"
                      tabIndex={0}
                      aria-pressed={selectedSection === 'operations'}
                      onKeyDown={(e) => e.key === 'Enter' && handleSectionSelect('operations')}
                    >
                      Operations Focus
                    </NavButton>
                    <NavButton 
                      selected={selectedSection === 'similar'}
                      onClick={() => handleSectionSelect('similar')}
                      role="button"
                      tabIndex={0}
                      aria-pressed={selectedSection === 'similar'}
                      onKeyDown={(e) => e.key === 'Enter' && handleSectionSelect('similar')}
                    >
                      Similar Problems
                    </NavButton>
                    <NavButton 
                      selected={selectedSection === 'toolbox'}
                      onClick={() => handleSectionSelect('toolbox')}
                      role="button"
                      tabIndex={0}
                      aria-pressed={selectedSection === 'toolbox'}
                      onKeyDown={(e) => e.key === 'Enter' && handleSectionSelect('toolbox')}
                    >
                      Problem Solving
                    </NavButton>
                  </SectionNavigation>
                </motion.div>
              )}
              
              {/* Problem Submission Area */}
              <div ref={problemSectionRef} id="problem-submission" aria-labelledby="problem-submission-title">
                <MainContent>
                  <motion.div 
                    initial={{ opacity: 0 }} 
                    animate={{ opacity: 1 }} 
                    transition={{ duration: 0.5, delay: 0.2 }}
                  >
                    <ScreenReaderOnly id="problem-submission-title">Problem Submission</ScreenReaderOnly>
                    <ProblemSubmissionArea 
                      onSubmit={handleProblemSubmit} 
                      onInputMethodSelected={handleInputMethodSelected}
                    />
                  </motion.div>
                </MainContent>
              </div>
              
              {/* Similar Problems & Solutions - Conditionally rendered */}
              {userProgress === 'submitted' && (selectedSection === 'similar' || !selectedSection) && (
                <MainContent>
                  <motion.div 
                    initial={{ opacity: 0 }} 
                    animate={{ opacity: 1 }} 
                    transition={{ duration: 0.5, delay: 0.4 }}
                  >
                    <Suspense fallback={
                      <LoadingFallback>
                        <CircularProgress size={30} color="secondary" />
                      </LoadingFallback>
                    }>
                      <SimilarProblems problemStatement={problemStatement} />
                    </Suspense>
                  </motion.div>
                </MainContent>
              )}
              
              {/* Selected Section Content - Conditionally rendered */}
              {userProgress === 'selected' && selectedSection && (
                <MainContent>
                  <motion.div 
                    initial={{ opacity: 0 }} 
                    animate={{ opacity: 1 }} 
                    transition={{ duration: 0.5 }}
                  >
                    {selectedSection === 'capital' && (
                      <Box>
                        <SidebarTitle>Capital Focus</SidebarTitle>
                        <Suspense fallback={
                          <LoadingFallback>
                            <CircularProgress size={30} color="secondary" />
                          </LoadingFallback>
                        }>
                          <ExecutiveBoard 
                            section="capital"
                            executives={capitalExecutives}
                            onSelectExecutive={(executive) => {
                              addNotification(`Selected ${executive.name}, ${executive.title}`, 'info');
                            }}
                          />
                        </Suspense>
                      </Box>
                    )}
                    {selectedSection === 'operations' && (
                      <Box>
                        <SidebarTitle>Operations Focus</SidebarTitle>
                        <Suspense fallback={
                          <LoadingFallback>
                            <CircularProgress size={30} color="secondary" />
                          </LoadingFallback>
                        }>
                          <ExecutiveBoard 
                            section="operations"
                            executives={operationsExecutives}
                            onSelectExecutive={(executive) => {
                              addNotification(`Selected ${executive.name}, ${executive.title}`, 'info');
                            }}
                          />
                        </Suspense>
                      </Box>
                    )}
                    {selectedSection === 'similar' && (
                      <Suspense fallback={
                        <LoadingFallback>
                          <CircularProgress size={30} color="secondary" />
                        </LoadingFallback>
                      }>
                        <SimilarProblems problemStatement={problemStatement} />
                      </Suspense>
                    )}
                    {selectedSection === 'toolbox' && (
                      <Suspense fallback={
                        <LoadingFallback>
                          <CircularProgress size={30} color="secondary" />
                        </LoadingFallback>
                      }>
                        <ProblemSolvingToolbox problemStatement={problemStatement} />
                      </Suspense>
                    )}
                  </motion.div>
                </MainContent>
              )}
            </Grid>
            
            {/* Right Sidebar - Operations Focus */}
            {showSidebars && (
              <Grid xs={12} md={3}>
                <RightSidebar isVisible={showSidebars}>
                  <SidebarTitle>Operations Focus</SidebarTitle>
                  <Suspense fallback={
                    <LoadingFallback>
                      <CircularProgress size={30} color="secondary" />
                    </LoadingFallback>
                  }>
                    <ExecutiveBoard 
                      section="operations"
                      executives={operationsExecutives}
                      onSelectExecutive={(executive) => {
                        addNotification(`Selected ${executive.name}, ${executive.title}`, 'info');
                      }}
                    />
                  </Suspense>
                </RightSidebar>
              </Grid>
            )}
            
            {/* Bottom Panel - Problem Solving Toolbox */}
            <Grid xs={12}>
              <Collapse in={userProgress === 'submitted' || userProgress === 'selected'}>
                <BottomPanel>
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }} 
                    animate={{ opacity: 1, y: 0 }} 
                    transition={{ duration: 0.5, delay: 0.6 }}
                  >
                    <Suspense fallback={
                      <LoadingFallback>
                        <CircularProgress size={30} color="secondary" />
                      </LoadingFallback>
                    }>
                      <ProblemSolvingToolbox problemStatement={problemStatement} />
                    </Suspense>
                  </motion.div>
                </BottomPanel>
              </Collapse>
            </Grid>
          </Grid>
        )}
      </ContentContainer>
      
      {/* Layout Toggle Button */}
      <Box sx={{ position: 'fixed', bottom: 16, right: 16, zIndex: 10 }}>
        <IconButton 
          onClick={() => setUseGridLayout(!useGridLayout)} 
          sx={{ 
            backgroundColor: 'rgba(138, 43, 226, 0.2)',
            color: '#fff',
            '&:hover': {
              backgroundColor: 'rgba(138, 43, 226, 0.4)',
            },
          }}
        >
          <Settings />
        </IconButton>
      </Box>
      
      {/* Adaptive Navigation */}
      <AdaptiveNavigation 
        problemStatement={problemStatement} 
        userProgress={userProgress}
        onBack={handleBack}
      />
      
      {/* Floating Notifications */}
      <FloatingNotifications notifications={notifications} />
    </PageContainer>
  );
};

export default Home; 